import Box from '@mui/material/Box';
import { type JSX, useEffect, useState } from 'react';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE, ICON_DELETE_REMOVE } from 'Config/Icons';
import { FolderTypes } from 'Models/Folders';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ResponseModel } from 'Models/ResponseModel';

import styles from './ManageFolderTypesModal.module.css';

export interface ManageFolderTypesModalProps {
    hideModal: () => void;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

/**
 * Modal for managing folder types for TPRM or UCRM document uploads, depending on `module`.
 * Note that there are separate state variables and functions for existing and new folder types. This is because existing folder types can only be deleted, not modified. Whereas new folder types can be added, modified, or deleted (from state).
 */
export const ManageFolderTypesModal = (props: ManageFolderTypesModalProps): JSX.Element => {
    const subHeader = `Manage folder types for ${props.module === ProductModule.UCRM ? 'AI Governance' : 'Third-Party Risk Management'} document uploads.`;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>();
    const [failureMessage, setFailureMessage] = useState<string>();
    const [existingFolderTypes, setExistingFolderTypes] = useState<string[]>([]);
    const [newFolderTypes, setNewFolderTypes] = useState<string[]>([]);

    useEffect(() => {
        const getExistingFolderTypes = async () => {
            try {
                const response: ResponseModel<FolderTypes> = await props.moduleApi.getFolderTypes();
                setExistingFolderTypes(response.data.type_set.sort());
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        getExistingFolderTypes();
    }, [props.moduleApi]);

    /**
     * Adds a new (empty) folder type for the user to fill in.
     */
    const handleNewFolderTypeAdd = (): void => {
        setNewFolderTypes([...newFolderTypes, '']);
    };

    /**
     * Removes a folder type from the list of existing folder types.
     */
    const handleExistingFolderTypeRemove = (index: number): void => {
        const folderTypes = [...existingFolderTypes];
        folderTypes.splice(index, 1);
        setExistingFolderTypes(folderTypes);
    };

    /**
     * Removes a folder type from the list of new folder types.
     */
    const handleNewFolderTypeRemove = (index: number): void => {
        const folderTypes = [...newFolderTypes];
        folderTypes.splice(index, 1);
        setNewFolderTypes(folderTypes);
    };

    /**
     * Sets the name for a new folder type.
     */
    const handleNewFolderTypeChange = (index: number, value: string): void => {
        const folderTypes = [...newFolderTypes];
        folderTypes[index] = value;
        setNewFolderTypes([...folderTypes]);
    };

    /**
     * Save the folder types additions/deletions.
     */
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        setFailureMessage(undefined);
        setSuccessMessage(undefined);

        // Trim the values of the new folder types.
        let combinedFolderTypes = newFolderTypes.map((folderType) => folderType.trim());
        // Combine the existing and new folder types.
        combinedFolderTypes = [...existingFolderTypes, ...combinedFolderTypes];
        // Eliminate duplicates.
        combinedFolderTypes = Array.from(new Set(combinedFolderTypes));
        // Eliminate new folder types that are empty strings.
        combinedFolderTypes = combinedFolderTypes.filter((folderType) => folderType !== '');
        // Sort the combined list of existing and new folder types.
        combinedFolderTypes = combinedFolderTypes.sort();
        // Form the request object.
        const request: FolderTypes = { type_set: combinedFolderTypes };

        try {
            setIsSubmitting(true);
            await props.moduleApi.updateFolderTypes(request);
            setSuccessMessage('Folder types saved.');
        } catch (error) {
            setFailureMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <Modal open isLoading closeModal={props.hideModal} header="Folder Types" subHeader={subHeader} failureMessage={failureMessage} />;
    }

    return (
        <Modal open header="Folder Types" subHeader={subHeader}>
            {successMessage && <Alert type="success">{successMessage}</Alert>}
            {failureMessage && <Alert type="error">{failureMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                {existingFolderTypes.length > 0 && (
                    <>
                        {existingFolderTypes.map((folderType, index) => {
                            return (
                                <div className={styles.folderTypeContainer} key={index}>
                                    <div className={styles.folderTypeName}>
                                        <FormFieldText disabled={true} formFieldType="text" formFieldId={`existingFolderType${index}`} formFieldLabel={`Folder Type`} value={folderType} />
                                    </div>
                                    <IconButton onClick={() => handleExistingFolderTypeRemove(index)} aria-label={`delete ${folderType}`} fontAwesomeImage={ICON_DELETE_REMOVE} />
                                </div>
                            );
                        })}
                    </>
                )}
                {newFolderTypes.length > 0 && (
                    <>
                        {newFolderTypes.map((folderType, index) => {
                            return (
                                <div className={styles.folderTypeContainer} key={index}>
                                    <div className={styles.folderTypeName}>
                                        <FormFieldText formFieldType="text" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleNewFolderTypeChange(index, event.currentTarget.value)} formFieldId={`newFolderType${index}`} formFieldLabel={`Folder Type`} required value={folderType || ''} />
                                    </div>
                                    <IconButton onClick={() => handleNewFolderTypeRemove(index)} aria-label={`delete ${folderType}`} fontAwesomeImage={ICON_DELETE_REMOVE} />
                                </div>
                            );
                        })}
                    </>
                )}
                <div>
                    <Button variant="linkText" size="lg" onClick={handleNewFolderTypeAdd}>
                        {'+ Add New Folder Type'}
                    </Button>
                </div>
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button variant="submit" isLoading={isSubmitting} loadingText="Saving...">
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
